/**
 * The steam gauges, drawn on canvases and used as textures.
 *
 * ## Why canvas and not geometry
 *
 * An instrument face is a *lot* of fine detail — tick marks, numerals, an arc of coloured
 * markings, a maker's name — over a disc about eight centimetres across. As geometry that is
 * hundreds of triangles per gauge and eight gauges, all of it aliasing badly at any angle; as a
 * texture it is one quad, and the detail is resolved by the mipmap chain rather than by the
 * triangle count.
 *
 * It also puts the whole instrument in one file per gauge rather than in a modelling tool, which
 * is what lets an airspeed indicator's white arc actually be the aircraft's Vs0…Vfe rather than a
 * decal that happens to look about right.
 *
 * ## The split that makes it affordable
 *
 * Each gauge is **two canvases**: a static face, drawn once at construction, and a dynamic layer
 * redrawn at 30 Hz carrying only the needles. The face is by far the more expensive — arcs, text,
 * dozens of ticks — and it never changes. So the per-frame cost is a `drawImage` of the cached
 * face plus two or three needle strokes, which is what keeps eight live instruments inside the
 * 0.3 ms budget the milestone sets.
 *
 * ## Needles are interpolated, not snapped
 *
 * A real instrument needle has mass and damping; a needle that jumps to the exact value every
 * update reads as digital no matter how well the face is drawn. Each needle is therefore a
 * critically-damped follower with its own time constant — an airspeed needle is quick, an
 * altimeter is quicker still, a vertical speed indicator genuinely lags by a second or two
 * because it is measuring a pressure *rate* through a calibrated leak, and the turn coordinator
 * is a gyro that responds in a fraction of a second. Those differences are not decoration: a VSI
 * that responded instantly would be a flight-path indicator, which is a different instrument.
 *
 * `updateFrame(alpha)`: yes — {@link InstrumentPanel.updateFrame}, self-throttled to 30 Hz.
 * Allocation-free per frame after construction: canvases, contexts and needle state are all made
 * once.
 */

import { DEG2RAD, clamp, expDamp } from '../core/math.js';
import { metresToFeet, mpsToFpm, mpsToKnots } from '../core/units.js';
import type { FlightState } from '../sim/fdm.js';

/** Face resolution [px]. 256 is a hair over one texel per screen pixel at a readable panel size. */
/*
 * 256, and raising it is not the one-line change it looks like.
 *
 * Every dial in this file is drawn with **hard-coded pixel offsets** authored against this
 * number — `this.centre - 40`, `lineWidth = 14`, `fillRect(this.centre + 24, …, 62, 24)` and
 * about thirty more. Only `this.radius` (line ~239) scales off `sizePx`, so doubling this draws
 * the artwork at half scale inside a face of nearly double the radius. Tried at 512 and reverted.
 *
 * Making the faces genuinely sharper means expressing every one of those offsets as a fraction of
 * `sizePx` first. That is worth doing — the discs measure 2.2–2.6 texels per screen pixel, so they
 * are permanently minified — but it is a deliberate pass over this whole file, not a constant.
 */
const FACE_PX = 256;

/** How often the needles are redrawn [Hz]. */
export const INSTRUMENT_REFRESH_HZ = 30;

/** Instrument-face colours, in one place so the panel reads as one instrument set. */
const INK = '#f2f4f7';
const DIM = '#9aa3ad';
const FACE = '#0c0f13';
const BEZEL = '#1b2027';
const NEEDLE = '#f5f7fa';
const WARNING = '#e0533f';
const CAUTION = '#e8c05a';
const SAFE = '#5fbf6b';

/** The gauges a trainer's panel carries. Six-pack first, in the standard T. */
export const GAUGE_IDS = [
  'airspeed',
  'attitude',
  'altimeter',
  'turn',
  'heading',
  'vsi',
  'tachometer',
  'fuel',
] as const;
export type GaugeId = (typeof GAUGE_IDS)[number];

/** Everything an instrument reads. Aviation units, converted once at this boundary. */
export interface InstrumentInputs {
  indicatedKt: number;
  altitudeFt: number;
  verticalSpeedFpm: number;
  headingDeg: number;
  pitchDeg: number;
  rollDeg: number;
  /** Turn rate [deg/s], positive right. A standard-rate turn is 3°/s. */
  turnRateDegPerS: number;
  /** Slip ball displacement, −1…1, positive when the ball is out to the right. */
  slipBall: number;
  rpm: number;
  /** Fuel in each tank, 0…1. */
  fuelLeft: number;
  fuelRight: number;
  /** Kollsman setting [inHg]. */
  altimeterSettingInHg: number;
}

export function makeInstrumentInputs(): InstrumentInputs {
  return {
    indicatedKt: 0,
    altitudeFt: 0,
    verticalSpeedFpm: 0,
    headingDeg: 0,
    pitchDeg: 0,
    rollDeg: 0,
    turnRateDegPerS: 0,
    slipBall: 0,
    rpm: 0,
    fuelLeft: 1,
    fuelRight: 1,
    altimeterSettingInHg: 29.92,
  };
}

/**
 * Read the instruments from the flight model.
 *
 * The one place SI becomes aviation units (CLAUDE.md: "aviation units only at the UI boundary").
 * The slip ball is derived rather than read: what a real ball measures is *lateral acceleration*,
 * which for a coordinated turn is zero and for a slip is proportional to sideslip — so β is the
 * honest source, scaled so a ball's width is about 5° of slip.
 */
export function readInstruments(state: FlightState, out: InstrumentInputs, fuelFraction = 1): InstrumentInputs {
  out.indicatedKt = mpsToKnots(state.indicatedAirspeedMps);
  out.altitudeFt = metresToFeet(state.altitudeM);
  out.verticalSpeedFpm = mpsToFpm(state.verticalSpeedMps);
  out.headingDeg = ((state.headingRad / DEG2RAD) % 360 + 360) % 360;
  out.pitchDeg = state.pitchRad / DEG2RAD;
  out.rollDeg = state.rollRad / DEG2RAD;

  /*
   * Turn rate from the bank angle, which is what the aeroplane is actually doing in a coordinated
   * turn: `ω = g·tan(φ)/V`. Reading the body yaw rate instead looks equivalent and is not — in a
   * slip the two disagree, and it is the *turn* the instrument shows, which is why a turn
   * coordinator and a slip ball are two indications on one face.
   */
  const speed = Math.max(5, state.trueAirspeedMps);
  out.turnRateDegPerS = ((9.80665 * Math.tan(clamp(state.rollRad, -1.4, 1.4))) / speed) / DEG2RAD;
  // A ball's width is about 5° of slip, so full deflection is a couple of ball widths.
  out.slipBall = clamp(-state.betaRad / DEG2RAD / 12, -1, 1);

  out.rpm = state.rpm;
  out.fuelLeft = clamp(fuelFraction, 0, 1);
  // A shade of asymmetry, because two needles that move as one look like one needle drawn twice.
  out.fuelRight = clamp(fuelFraction * 0.96, 0, 1);
  return out;
}

/** A needle that follows its value the way a real one does. */
class Needle {
  private value: number;
  private readonly halfLifeS: number;

  constructor(initial: number, halfLifeS: number) {
    this.value = initial;
    this.halfLifeS = halfLifeS;
  }

  get current(): number {
    return this.value;
  }

  updateFrame(targetValue: number, dtSeconds: number): number {
    this.value = expDamp(this.value, targetValue, this.halfLifeS, dtSeconds);
    return this.value;
  }

  /** Snap, e.g. on a respawn — a needle that swept across the face would look like a fault. */
  set(value: number): void {
    this.value = value;
  }
}

/**
 * A needle that follows a *circular* value the short way round.
 *
 * Without this a heading indicator crossing north sweeps 359° the wrong way, once per circuit,
 * which is the single most obvious way a compass card gives itself away.
 */
class CircularNeedle {
  private value = 0;
  private readonly halfLifeS: number;

  constructor(halfLifeS: number) {
    this.halfLifeS = halfLifeS;
  }

  get current(): number {
    return this.value;
  }

  updateFrame(targetDeg: number, dtSeconds: number): number {
    let delta = targetDeg - this.value;
    delta -= Math.floor(delta / 360 + 0.5) * 360;
    this.value = expDamp(this.value, this.value + delta, this.halfLifeS, dtSeconds);
    return this.value;
  }

  set(valueDeg: number): void {
    this.value = valueDeg;
  }
}

/** Aircraft-specific markings for the airspeed indicator's arcs [kt]. */
export interface AirspeedMarkings {
  /** Bottom of the white arc — stall, full flap. */
  vs0: number;
  /** Bottom of the green arc — stall, clean. */
  vs1: number;
  /** Top of the white arc — maximum flap extended. */
  vfe: number;
  /** Top of the green arc — maximum structural cruising. */
  vno: number;
  /** Top of the yellow arc, the red line — never exceed. */
  vne: number;
  /** Full-scale deflection. */
  maximum: number;
}

export interface GaugeOptions {
  airspeed: AirspeedMarkings;
  /** Redline and the green arc for the tachometer [rpm]. */
  tachometer: { greenLow: number; greenHigh: number; redline: number; maximum: number };
}

/**
 * One instrument: a cached face and a live needle layer.
 *
 * `canvas` is the composited result and is what the material samples. It is redrawn by blitting
 * the cached face and then stroking the needles, which is roughly twenty times cheaper than
 * redrawing the face.
 */
abstract class Gauge {
  readonly canvas: HTMLCanvasElement;
  protected readonly ctx: CanvasRenderingContext2D;
  private readonly face: HTMLCanvasElement;
  protected readonly radius: number;
  protected readonly centre: number;

  constructor(sizePx = FACE_PX) {
    this.canvas = makeCanvas(sizePx);
    this.ctx = context2d(this.canvas);
    this.face = makeCanvas(sizePx);
    this.centre = sizePx / 2;
    this.radius = sizePx / 2 - 6;

    const faceCtx = context2d(this.face);
    drawBezel(faceCtx, this.centre, this.radius);
    this.drawFace(faceCtx);
  }

  /** Draw the parts that never change. Called once. */
  protected abstract drawFace(ctx: CanvasRenderingContext2D): void;

  /** Advance the needles and draw them. Called at {@link INSTRUMENT_REFRESH_HZ}. */
  abstract updateFrame(inputs: InstrumentInputs, dtSeconds: number): void;

  /** Snap every needle to its input, e.g. on a respawn. */
  abstract snap(inputs: InstrumentInputs): void;

  /** Blit the cached face. Every `updateFrame` starts here. */
  protected beginFrame(): CanvasRenderingContext2D {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.face, 0, 0);
    return ctx;
  }
}

/**
 * Airspeed indicator.
 *
 * The arcs are the *aircraft's* — Vs0 to Vfe white, Vs1 to Vno green, Vno to Vne yellow, Vne a red
 * line — read from the config rather than drawn to look about right, which is the difference
 * between an instrument and a picture of one. The scale starts at Vs0 and is non-linear at the
 * bottom on a real ASI; this one is linear, because the non-linearity is a property of the
 * pitot-static mechanism rather than something a pilot reads.
 */
class AirspeedIndicator extends Gauge {
  private readonly needle = new Needle(0, 0.09);
  private readonly markings: AirspeedMarkings;

  constructor(markings: AirspeedMarkings) {
    // Field assignment has to happen before `super` runs `drawFace`, which is not allowed — so
    // the markings are stashed on a module-level slot the constructor picks up. See `pending`.
    pendingAirspeed = markings;
    super();
    this.markings = markings;
  }

  private angleFor(kt: number): number {
    // 30 kt at the bottom-left, sweeping 300° clockwise to the maximum.
    const m = pendingAirspeed;
    const span = m.maximum - 30;
    return (-150 + (clamp(kt, 0, m.maximum) - 30) / span * 300) * DEG2RAD;
  }

  protected drawFace(ctx: CanvasRenderingContext2D): void {
    const m = pendingAirspeed;
    const arc = (from: number, to: number, colour: string, width: number, inset: number): void => {
      ctx.beginPath();
      ctx.strokeStyle = colour;
      ctx.lineWidth = width;
      ctx.arc(this.centre, this.centre, this.radius - inset, this.angleFor(from), this.angleFor(to));
      ctx.stroke();
    };

    arc(m.vs0, m.vfe, '#e9edf2', 7, 20);
    arc(m.vs1, m.vno, SAFE, 9, 11);
    arc(m.vno, m.vne, CAUTION, 9, 11);
    arc(m.vne, Math.min(m.maximum, m.vne + 6), WARNING, 9, 11);

    ctx.strokeStyle = INK;
    ctx.fillStyle = INK;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let kt = 30; kt <= m.maximum; kt += 10) {
      const major = kt % 20 === 0;
      const angle = this.angleFor(kt);
      tick(ctx, this.centre, this.radius - 18, angle, major ? 13 : 8, major ? 2.4 : 1.4, INK);
      if (major) {
        ctx.font = '600 20px system-ui, sans-serif';
        label(ctx, this.centre, this.radius - 44, angle, String(kt));
      }
    }

    ctx.fillStyle = DIM;
    ctx.font = '600 13px system-ui, sans-serif';
    ctx.fillText('AIRSPEED', this.centre, this.centre - 40);
    ctx.font = '500 11px system-ui, sans-serif';
    ctx.fillText('KNOTS', this.centre, this.centre + 46);
  }

  updateFrame(inputs: InstrumentInputs, dtSeconds: number): void {
    const kt = this.needle.updateFrame(inputs.indicatedKt, dtSeconds);
    const ctx = this.beginFrame();
    drawNeedle(ctx, this.centre, this.angleFor(kt), this.radius - 22, NEEDLE);
    void this.markings;
  }

  snap(inputs: InstrumentInputs): void {
    this.needle.set(inputs.indicatedKt);
  }
}

/** Set immediately before `super()` so `drawFace` can see it. See {@link AirspeedIndicator}. */
let pendingAirspeed: AirspeedMarkings = { vs0: 40, vs1: 48, vfe: 110, vno: 129, vne: 163, maximum: 180 };
let pendingTach: GaugeOptions['tachometer'] = {
  greenLow: 2100,
  greenHigh: 2500,
  redline: 2700,
  maximum: 3000,
};

/**
 * Attitude indicator.
 *
 * The one gauge whose face *moves*: the horizon and the pitch ladder rotate and translate behind
 * a fixed aircraft symbol and a bank pointer. So its "face" is only the bezel and the bank scale,
 * and everything else is drawn live inside a circular clip.
 */
class AttitudeIndicator extends Gauge {
  private readonly pitch = new Needle(0, 0.04);
  private readonly roll = new Needle(0, 0.04);

  protected drawFace(ctx: CanvasRenderingContext2D): void {
    // The bank scale, on the bezel, which is the only static part.
    ctx.strokeStyle = INK;
    for (const bankDeg of [-60, -45, -30, -20, -10, 0, 10, 20, 30, 45, 60]) {
      const angle = (-90 + bankDeg) * DEG2RAD;
      const major = bankDeg % 30 === 0 || bankDeg === 0;
      tick(ctx, this.centre, this.radius - 2, angle, major ? 11 : 7, major ? 2.6 : 1.6, INK);
    }
    ctx.fillStyle = DIM;
    ctx.font = '600 13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('ATTITUDE', this.centre, this.centre + this.radius - 26);
  }

  updateFrame(inputs: InstrumentInputs, dtSeconds: number): void {
    const pitchDeg = this.pitch.updateFrame(inputs.pitchDeg, dtSeconds);
    const rollDeg = this.roll.updateFrame(inputs.rollDeg, dtSeconds);
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    ctx.save();
    ctx.beginPath();
    ctx.arc(this.centre, this.centre, this.radius - 14, 0, Math.PI * 2);
    ctx.clip();

    ctx.translate(this.centre, this.centre);
    ctx.rotate(-rollDeg * DEG2RAD);
    // Pixels of ladder per degree of pitch. 2.4 puts ±30° comfortably inside the instrument.
    const perDeg = 2.4;
    ctx.translate(0, pitchDeg * perDeg);

    const span = this.radius * 3;
    ctx.fillStyle = '#3f7bbf';
    ctx.fillRect(-span, -span, span * 2, span);
    ctx.fillStyle = '#6b4a2c';
    ctx.fillRect(-span, 0, span * 2, span);
    ctx.fillStyle = INK;
    ctx.fillRect(-span, -1.5, span * 2, 3);

    ctx.strokeStyle = INK;
    ctx.fillStyle = INK;
    ctx.lineWidth = 2;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '600 11px system-ui, sans-serif';
    for (let deg = -30; deg <= 30; deg += 10) {
      if (deg === 0) continue;
      const y = -deg * perDeg;
      const half = deg % 20 === 0 ? 32 : 18;
      ctx.beginPath();
      ctx.moveTo(-half, y);
      ctx.lineTo(half, y);
      ctx.stroke();
      if (deg % 20 === 0) {
        ctx.fillText(String(Math.abs(deg)), -half - 12, y);
        ctx.fillText(String(Math.abs(deg)), half + 12, y);
      }
    }
    ctx.restore();

    // The bezel and bank scale go back on top of the moving horizon.
    ctx.save();
    ctx.beginPath();
    ctx.arc(this.centre, this.centre, this.radius - 14, 0, Math.PI * 2);
    ctx.strokeStyle = BEZEL;
    ctx.lineWidth = 14;
    ctx.stroke();
    ctx.restore();

    // The bank pointer, which moves with the aeroplane…
    ctx.save();
    ctx.translate(this.centre, this.centre);
    ctx.rotate(-rollDeg * DEG2RAD);
    ctx.fillStyle = CAUTION;
    ctx.beginPath();
    ctx.moveTo(0, -(this.radius - 16));
    ctx.lineTo(-8, -(this.radius - 4));
    ctx.lineTo(8, -(this.radius - 4));
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // …and the aircraft symbol, which does not.
    ctx.strokeStyle = CAUTION;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(this.centre - 46, this.centre);
    ctx.lineTo(this.centre - 16, this.centre);
    ctx.moveTo(this.centre + 16, this.centre);
    ctx.lineTo(this.centre + 46, this.centre);
    ctx.moveTo(this.centre, this.centre - 4);
    ctx.lineTo(this.centre, this.centre + 8);
    ctx.stroke();
  }

  snap(inputs: InstrumentInputs): void {
    this.pitch.set(inputs.pitchDeg);
    this.roll.set(inputs.rollDeg);
  }
}

/**
 * Altimeter, with a Kollsman window.
 *
 * Three hands on a real sensitive altimeter; two here — hundreds and thousands — because the
 * ten-thousands pointer is a striped wedge that is unreadable at panel size and this aeroplane
 * cannot reach 10,000 ft in a hurry anyway. The Kollsman window shows the setting in inches of
 * mercury, which is what the pilot dials in and the only reason the instrument is not a
 * pressure gauge.
 */
class Altimeter extends Gauge {
  private readonly feet = new Needle(0, 0.035);

  protected drawFace(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let hundred = 0; hundred < 10; hundred++) {
      const angle = (-90 + hundred * 36) * DEG2RAD;
      tick(ctx, this.centre, this.radius - 14, angle, 14, 2.6, INK);
      ctx.fillStyle = INK;
      ctx.font = '600 22px system-ui, sans-serif';
      label(ctx, this.centre, this.radius - 44, angle, String(hundred));
      for (let fifth = 1; fifth < 5; fifth++) {
        tick(ctx, this.centre, this.radius - 14, angle + fifth * 7.2 * DEG2RAD, 7, 1.4, DIM);
      }
    }
    ctx.fillStyle = DIM;
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.fillText('ALTITUDE', this.centre, this.centre - 46);
    ctx.font = '500 10px system-ui, sans-serif';
    ctx.fillText('FEET', this.centre, this.centre - 32);
  }

  updateFrame(inputs: InstrumentInputs, dtSeconds: number): void {
    const feet = this.feet.updateFrame(inputs.altitudeFt, dtSeconds);
    const ctx = this.beginFrame();

    // The Kollsman window: a cut-out on the right of the face.
    ctx.fillStyle = '#05070a';
    ctx.fillRect(this.centre + 24, this.centre - 12, 62, 24);
    ctx.strokeStyle = DIM;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(this.centre + 24, this.centre - 12, 62, 24);
    ctx.fillStyle = INK;
    ctx.font = '600 16px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(inputs.altimeterSettingInHg.toFixed(2), this.centre + 55, this.centre + 1);

    // Thousands: short and fat. Hundreds: long and thin. The convention every altimeter uses.
    const thousandsAngle = (-90 + (feet / 1000) * 36) * DEG2RAD;
    const hundredsAngle = (-90 + ((feet % 1000) / 100) * 36) * DEG2RAD;
    drawNeedle(ctx, this.centre, thousandsAngle, this.radius - 92, NEEDLE, 8);
    drawNeedle(ctx, this.centre, hundredsAngle, this.radius - 26, NEEDLE, 4.5);
  }

  snap(inputs: InstrumentInputs): void {
    this.feet.set(inputs.altitudeFt);
  }
}

/**
 * Turn coordinator: a miniature aeroplane that banks with the turn rate, and a slip ball.
 *
 * The two indications are independent and that is the point of the instrument — the aeroplane
 * shows *rate of turn* and the ball shows *whether the turn is coordinated*. A pilot reads them
 * together ("standard rate, ball centred") and a simulator that drove both from the same number
 * would have removed the only thing the instrument is for.
 */
class TurnCoordinator extends Gauge {
  private readonly rate = new Needle(0, 0.12);
  private readonly ball = new Needle(0, 0.18);

  protected drawFace(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Standard-rate marks: a two-minute turn is 3°/s, which sits at ±17° of miniature-aeroplane
    // bank on this instrument.
    for (const side of [-1, 1]) {
      const angle = (-90 + side * 17) * DEG2RAD;
      tick(ctx, this.centre, this.radius - 8, angle, 16, 3, INK);
      ctx.fillStyle = DIM;
      ctx.font = '600 12px system-ui, sans-serif';
      label(ctx, this.centre, this.radius - 34, angle, side < 0 ? 'L' : 'R');
    }
    ctx.fillStyle = DIM;
    ctx.font = '600 11px system-ui, sans-serif';
    ctx.fillText('2 MIN', this.centre, this.centre - 54);
    ctx.fillText('TURN COORDINATOR', this.centre, this.centre + 34);
    ctx.font = '500 10px system-ui, sans-serif';
    ctx.fillText('NO PITCH INFORMATION', this.centre, this.centre + 48);

    // The inclinometer tube.
    ctx.beginPath();
    ctx.strokeStyle = '#2a3038';
    ctx.lineWidth = 16;
    ctx.lineCap = 'round';
    ctx.arc(this.centre, this.centre - 44, 76, 58 * DEG2RAD, 122 * DEG2RAD);
    ctx.stroke();
    ctx.lineCap = 'butt';
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.strokeStyle = INK;
      ctx.lineWidth = 1.6;
      ctx.moveTo(this.centre + side * 9, this.centre + 24);
      ctx.lineTo(this.centre + side * 9, this.centre + 40);
      ctx.stroke();
    }
  }

  updateFrame(inputs: InstrumentInputs, dtSeconds: number): void {
    const rate = this.rate.updateFrame(inputs.turnRateDegPerS, dtSeconds);
    const ball = this.ball.updateFrame(inputs.slipBall, dtSeconds);
    const ctx = this.beginFrame();

    // The miniature aeroplane: 17° of bank per 3°/s of turn, clamped so it stays on the face.
    const bankDeg = clamp((rate / 3) * 17, -40, 40);
    ctx.save();
    ctx.translate(this.centre, this.centre - 12);
    ctx.rotate(bankDeg * DEG2RAD);
    ctx.fillStyle = INK;
    ctx.fillRect(-52, -2.5, 104, 5);
    ctx.fillRect(-4, -12, 8, 12);
    ctx.beginPath();
    ctx.arc(0, 0, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // The ball, sliding along its curved tube.
    const ballAngle = (90 + clamp(ball, -1, 1) * 28) * DEG2RAD;
    ctx.beginPath();
    ctx.fillStyle = '#0e1216';
    ctx.arc(
      this.centre + Math.cos(ballAngle) * 76,
      this.centre - 44 + Math.sin(ballAngle) * 76,
      7,
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  snap(inputs: InstrumentInputs): void {
    this.rate.set(inputs.turnRateDegPerS);
    this.ball.set(inputs.slipBall);
  }
}

/**
 * Heading indicator: a compass card that turns under a fixed lubber line.
 *
 * The card is drawn live rather than cached, because a cached card would have to be rotated —
 * and a rotated bitmap of small numerals at 256 px is a blurry mess. Drawing the numerals in
 * their rotated frame keeps them crisp, which is the whole reason to use canvas.
 */
class HeadingIndicator extends Gauge {
  private readonly heading = new CircularNeedle(0.07);

  protected drawFace(ctx: CanvasRenderingContext2D): void {
    // Only the lubber line and the aircraft symbol are fixed.
    ctx.fillStyle = CAUTION;
    ctx.beginPath();
    ctx.moveTo(this.centre, this.centre - this.radius + 4);
    ctx.lineTo(this.centre - 7, this.centre - this.radius + 18);
    ctx.lineTo(this.centre + 7, this.centre - this.radius + 18);
    ctx.closePath();
    ctx.fill();
  }

  updateFrame(inputs: InstrumentInputs, dtSeconds: number): void {
    const headingDeg = this.heading.updateFrame(inputs.headingDeg, dtSeconds);
    const ctx = this.beginFrame();

    ctx.save();
    ctx.translate(this.centre, this.centre);
    ctx.rotate(-headingDeg * DEG2RAD);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let deg = 0; deg < 360; deg += 5) {
      const angle = (deg - 90) * DEG2RAD;
      const major = deg % 30 === 0;
      tick(ctx, 0, this.radius - 20, angle, major ? 13 : 7, major ? 2.4 : 1.3, major ? INK : DIM);
      if (major) {
        const text = deg === 0 ? 'N' : deg === 90 ? 'E' : deg === 180 ? 'S' : deg === 270 ? 'W' : String(deg / 10);
        ctx.save();
        // Rotate the numeral into its own upright frame so it is drawn crisp, not resampled.
        const x = Math.cos(angle) * (this.radius - 46);
        const y = Math.sin(angle) * (this.radius - 46);
        ctx.translate(x, y);
        ctx.rotate(deg * DEG2RAD);
        ctx.fillStyle = INK;
        ctx.font = deg % 90 === 0 ? '700 22px system-ui, sans-serif' : '600 19px system-ui, sans-serif';
        ctx.fillText(text, 0, 0);
        ctx.restore();
      }
    }
    ctx.restore();

    // The aeroplane symbol, fixed, pointing at the lubber line.
    ctx.strokeStyle = INK;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(this.centre, this.centre - 26);
    ctx.lineTo(this.centre, this.centre + 24);
    ctx.moveTo(this.centre - 24, this.centre - 2);
    ctx.lineTo(this.centre + 24, this.centre - 2);
    ctx.moveTo(this.centre - 10, this.centre + 18);
    ctx.lineTo(this.centre + 10, this.centre + 18);
    ctx.stroke();
  }

  snap(inputs: InstrumentInputs): void {
    this.heading.set(inputs.headingDeg);
  }
}

/**
 * Vertical speed indicator.
 *
 * The slow one, deliberately. A real VSI measures the rate at which a calibrated leak equalises
 * a diaphragm against static pressure, which takes a second or two to settle — so it lags, and
 * pilots are taught to lead it. A VSI that responded instantly would be a flight-path indicator
 * and would let a pilot fly a precision approach the instrument does not actually support.
 */
class VerticalSpeedIndicator extends Gauge {
  private readonly fpm = new Needle(0, 0.55);

  private angleFor(fpm: number): number {
    // ±2000 fpm over ±165°, zero on the left-hand horizontal.
    return (180 + (clamp(fpm, -2000, 2000) / 2000) * -165) * DEG2RAD;
  }

  protected drawFace(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let fpm = -2000; fpm <= 2000; fpm += 100) {
      const major = fpm % 500 === 0;
      const angle = this.angleFor(fpm);
      tick(ctx, this.centre, this.radius - 14, angle, major ? 13 : 7, major ? 2.4 : 1.3, major ? INK : DIM);
      if (fpm % 1000 === 0) {
        ctx.fillStyle = INK;
        ctx.font = '600 21px system-ui, sans-serif';
        label(ctx, this.centre, this.radius - 44, angle, String(Math.abs(fpm) / 1000));
      }
    }
    ctx.fillStyle = DIM;
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.fillText('VERTICAL SPEED', this.centre, this.centre - 44);
    ctx.font = '500 10px system-ui, sans-serif';
    ctx.fillText('100 FT / MIN', this.centre, this.centre + 46);
    ctx.fillText('UP', this.centre + 46, this.centre - 22);
    ctx.fillText('DOWN', this.centre + 46, this.centre + 22);
  }

  updateFrame(inputs: InstrumentInputs, dtSeconds: number): void {
    const fpm = this.fpm.updateFrame(inputs.verticalSpeedFpm, dtSeconds);
    const ctx = this.beginFrame();
    drawNeedle(ctx, this.centre, this.angleFor(fpm), this.radius - 22, NEEDLE);
  }

  snap(inputs: InstrumentInputs): void {
    this.fpm.set(inputs.verticalSpeedFpm);
  }
}

/** Tachometer, with the aircraft's own green arc and redline. */
class Tachometer extends Gauge {
  private readonly rpm = new Needle(0, 0.08);

  private angleFor(rpm: number): number {
    const m = pendingTach;
    return (-120 + (clamp(rpm, 0, m.maximum) / m.maximum) * 240) * DEG2RAD;
  }

  protected drawFace(ctx: CanvasRenderingContext2D): void {
    const m = pendingTach;
    ctx.beginPath();
    ctx.strokeStyle = SAFE;
    ctx.lineWidth = 9;
    ctx.arc(this.centre, this.centre, this.radius - 12, this.angleFor(m.greenLow), this.angleFor(m.greenHigh));
    ctx.stroke();
    ctx.beginPath();
    ctx.strokeStyle = WARNING;
    ctx.arc(this.centre, this.centre, this.radius - 12, this.angleFor(m.redline), this.angleFor(m.maximum));
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let rpm = 0; rpm <= m.maximum; rpm += 100) {
      const major = rpm % 500 === 0;
      const angle = this.angleFor(rpm);
      tick(ctx, this.centre, this.radius - 20, angle, major ? 12 : 6, major ? 2.2 : 1.2, major ? INK : DIM);
      if (rpm % 500 === 0) {
        ctx.fillStyle = INK;
        ctx.font = '600 19px system-ui, sans-serif';
        label(ctx, this.centre, this.radius - 46, angle, String(rpm / 100));
      }
    }
    ctx.fillStyle = DIM;
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.fillText('RPM', this.centre, this.centre - 44);
    ctx.font = '500 10px system-ui, sans-serif';
    ctx.fillText('HUNDREDS', this.centre, this.centre + 52);
  }

  updateFrame(inputs: InstrumentInputs, dtSeconds: number): void {
    const rpm = this.rpm.updateFrame(inputs.rpm, dtSeconds);
    const ctx = this.beginFrame();
    drawNeedle(ctx, this.centre, this.angleFor(rpm), this.radius - 26, NEEDLE);
    ctx.fillStyle = INK;
    ctx.font = '600 15px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`${Math.round(rpm)}`, this.centre, this.centre + 32);
  }

  snap(inputs: InstrumentInputs): void {
    this.rpm.set(inputs.rpm);
  }
}

/** Fuel quantity — two needles on one face, left and right tanks. */
class FuelGauge extends Gauge {
  private readonly left = new Needle(1, 0.6);
  private readonly right = new Needle(1, 0.6);

  private angleFor(fraction: number, side: number): number {
    // Two 100° arcs, left tank sweeping up the left side and right tank up the right.
    return (side < 0 ? -150 + clamp(fraction, 0, 1) * 100 : 150 - clamp(fraction, 0, 1) * 100) * DEG2RAD;
  }

  protected drawFace(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const side of [-1, 1]) {
      for (const [fraction, text] of [[0, 'E'], [0.25, ''], [0.5, '½'], [0.75, ''], [1, 'F']] as const) {
        const angle = this.angleFor(fraction, side);
        tick(ctx, this.centre, this.radius - 16, angle, text === '' ? 7 : 12, text === '' ? 1.3 : 2.4, INK);
        if (text !== '') {
          ctx.fillStyle = fraction === 0 ? WARNING : INK;
          ctx.font = '600 18px system-ui, sans-serif';
          label(ctx, this.centre, this.radius - 42, angle, text);
        }
      }
      ctx.fillStyle = DIM;
      ctx.font = '600 12px system-ui, sans-serif';
      ctx.fillText(side < 0 ? 'L' : 'R', this.centre + side * 40, this.centre + 34);
    }
    ctx.fillStyle = DIM;
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.fillText('FUEL', this.centre, this.centre - 40);
    ctx.font = '500 10px system-ui, sans-serif';
    ctx.fillText('US GAL', this.centre, this.centre - 26);
  }

  updateFrame(inputs: InstrumentInputs, dtSeconds: number): void {
    const left = this.left.updateFrame(inputs.fuelLeft, dtSeconds);
    const right = this.right.updateFrame(inputs.fuelRight, dtSeconds);
    const ctx = this.beginFrame();
    drawNeedle(ctx, this.centre, this.angleFor(left, -1), this.radius - 24, left < 0.12 ? WARNING : NEEDLE, 4);
    drawNeedle(ctx, this.centre, this.angleFor(right, 1), this.radius - 24, right < 0.12 ? WARNING : NEEDLE, 4);
  }

  snap(inputs: InstrumentInputs): void {
    this.left.set(inputs.fuelLeft);
    this.right.set(inputs.fuelRight);
  }
}

/**
 * The whole instrument set, refreshed together at 30 Hz.
 *
 * 30 rather than every frame because a needle is a physical object with a settling time measured
 * in tenths of a second, and nothing on a panel changes fast enough to need 120 updates a second.
 * It halves the panel's cost at 60 fps and quarters it at 120, which is the difference between
 * the gauges fitting in the 0.3 ms budget and not.
 *
 * The *interpolation* still uses the real elapsed time between redraws, so the needle motion is
 * identical whatever the refresh rate — a needle that moved in 30 Hz steps would defeat the point.
 */
export class InstrumentPanel {
  private readonly gauges: Map<GaugeId, Gauge>;
  private nextUpdateMs = 0;
  private lastUpdateMs = 0;
  /** Set when a gauge canvas has been redrawn, so the texture knows to re-upload. */
  private dirtyValue = false;

  constructor(options: GaugeOptions) {
    pendingAirspeed = options.airspeed;
    pendingTach = options.tachometer;
    this.gauges = new Map<GaugeId, Gauge>([
      ['airspeed', new AirspeedIndicator(options.airspeed)],
      ['attitude', new AttitudeIndicator()],
      ['altimeter', new Altimeter()],
      ['turn', new TurnCoordinator()],
      ['heading', new HeadingIndicator()],
      ['vsi', new VerticalSpeedIndicator()],
      ['tachometer', new Tachometer()],
      ['fuel', new FuelGauge()],
    ]);
  }

  /** The canvas for one gauge — what a material samples. */
  canvasFor(id: GaugeId): HTMLCanvasElement {
    const gauge = this.gauges.get(id);
    if (gauge === undefined) throw new Error(`no such gauge: ${id}`);
    return gauge.canvas;
  }

  /** True when a redraw happened since this was last read. Clears on read. */
  takeDirty(): boolean {
    const dirty = this.dirtyValue;
    this.dirtyValue = false;
    return dirty;
  }

  /**
   * Redraw the needles, at most {@link INSTRUMENT_REFRESH_HZ} times a second.
   *
   * @returns true when a redraw actually happened.
   */
  updateFrame(nowMs: number, inputs: InstrumentInputs): boolean {
    if (nowMs < this.nextUpdateMs) return false;
    // Elapsed time since the *last redraw*, not since the last frame — the needles integrate on
    // their own clock so their motion does not depend on the display's refresh rate.
    const dtSeconds = this.lastUpdateMs === 0 ? 1 / INSTRUMENT_REFRESH_HZ : (nowMs - this.lastUpdateMs) / 1000;
    this.lastUpdateMs = nowMs;
    this.nextUpdateMs = nowMs + 1000 / INSTRUMENT_REFRESH_HZ;

    for (const gauge of this.gauges.values()) gauge.updateFrame(inputs, Math.min(0.25, dtSeconds));
    this.dirtyValue = true;
    return true;
  }

  /** Snap every needle to its input — on a respawn, so nothing sweeps across the face. */
  snap(inputs: InstrumentInputs): void {
    for (const gauge of this.gauges.values()) {
      gauge.snap(inputs);
      gauge.updateFrame(inputs, 1);
    }
    this.dirtyValue = true;
  }
}

// --- drawing helpers -----------------------------------------------------------------------

function makeCanvas(sizePx: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = sizePx;
  canvas.height = sizePx;
  return canvas;
}

function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('2D canvas context unavailable — instruments cannot be drawn');
  return ctx;
}

/** The instrument case: a dark face inside a raised bezel. */
function drawBezel(ctx: CanvasRenderingContext2D, centre: number, radius: number): void {
  ctx.beginPath();
  ctx.fillStyle = FACE;
  ctx.arc(centre, centre, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.strokeStyle = BEZEL;
  ctx.lineWidth = 10;
  ctx.arc(centre, centre, radius - 1, 0, Math.PI * 2);
  ctx.stroke();
}

/** One tick mark, `length` px long, ending at `outerRadius` from the centre. */
function tick(
  ctx: CanvasRenderingContext2D,
  centre: number,
  outerRadius: number,
  angleRad: number,
  length: number,
  width: number,
  colour: string,
): void {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  ctx.beginPath();
  ctx.strokeStyle = colour;
  ctx.lineWidth = width;
  ctx.moveTo(centre + cos * outerRadius, centre + sin * outerRadius);
  ctx.lineTo(centre + cos * (outerRadius - length), centre + sin * (outerRadius - length));
  ctx.stroke();
}

/** A numeral at `radius` from the centre, always upright. */
function label(
  ctx: CanvasRenderingContext2D,
  centre: number,
  radius: number,
  angleRad: number,
  text: string,
): void {
  ctx.fillText(text, centre + Math.cos(angleRad) * radius, centre + Math.sin(angleRad) * radius);
}

/** A needle from the hub outward, with a counterweight tail and a hub cap. */
function drawNeedle(
  ctx: CanvasRenderingContext2D,
  centre: number,
  angleRad: number,
  length: number,
  colour: string,
  width = 5,
): void {
  ctx.save();
  ctx.translate(centre, centre);
  ctx.rotate(angleRad);
  ctx.fillStyle = colour;
  ctx.beginPath();
  // A taper: wide at the hub, pointed at the tip. A parallel-sided needle looks like a stick.
  ctx.moveTo(-length * 0.18, -width * 0.45);
  ctx.lineTo(length, -width * 0.18);
  ctx.lineTo(length, width * 0.18);
  ctx.lineTo(-length * 0.18, width * 0.45);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.fillStyle = '#1c222a';
  ctx.arc(centre, centre, width * 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1.2;
  ctx.stroke();
}
